// A mail that needs a human decision (no safe assignment, or an inquiry before grading): candidates, assignment to a
// deal, new deal, kein Bezug; original and Outlook link. Used by the views Zuordnen and Anfragen.
import type { Mail } from "../api";
import { S, write } from "../state";
import { esc, fmtDT, toast, today } from "../util";
import { dealOptions } from "./board";
import { showOriginal } from "./original";

const MAILBOX: Record<string, string> = { sales: "sales@", info: "info@", frank: "Postfach F. Elvers" };

export function mailCard(m: Mail, opts: { allowAnfrage: boolean; allowUnklar: boolean }): string {
  const ro = S.readOnly;
  const box = m.locations.map((l) => MAILBOX[l.mailbox] ?? l.mailbox).join(", ");
  const web = m.locations.find((l) => l.webLink)?.webLink;
  return `<article class="kf" data-mail="${esc(m.id)}" data-version="${m.version}">
      <div class="meta"><span class="dir">${m.direction === "out" ? "AUS" : "EIN"}</span><span>${esc(m.fromName || m.fromAddr)}${m.fromName ? ` &lt;${esc(m.fromAddr)}&gt;` : ""}</span><span class="mono">${fmtDT(m.receivedAt)}</span><span>${esc(box)}</span></div>
      <div class="subj">${esc(m.subject)}</div>
      ${m.summary ? `<p style="margin:0">${esc(m.summary)}</p>` : ""}
      ${m.reason ? `<p class="muted" style="margin:0;font-size:12.5px">KI: ${esc(m.reason)}</p>` : ""}
      ${m.candidates.length ? `<div class="cands">${m.candidates.map((c) => `<button class="btn" data-assign="${esc(c.dealId)}" ${ro ? "disabled" : ""} title="${esc(c.reason)}">${esc(c.company)} · ${esc(c.name)} <small>${Math.round(c.score * 100)} %</small></button>`).join("")}</div>` : ""}
      <div class="actions">
        <select aria-label="Einem Deal zuordnen" data-pick ${ro ? "disabled" : ""}>${dealOptions("", true)}</select>
        ${opts.allowAnfrage ? `<button class="btn" data-decide="anfrage" ${ro ? "disabled" : ""}>Neue Anfrage</button>` : ""}
        ${opts.allowUnklar ? `<button class="btn" data-decide="unklar" ${ro ? "disabled" : ""} title="Die Mail kommt unter „Mails zuordnen“.">Später zuordnen</button>` : ""}
        <button class="btn" data-decide="kein_bezug" ${ro ? "disabled" : ""} title="Die Mail wird nicht gespeichert, sie bleibt nur im Postfach.">Kein Bezug</button>
        <button class="btn" data-original>Original</button>
        ${web ? `<a class="btn" href="${esc(web)}" target="_blank" rel="noopener">In Outlook öffnen</a>` : ""}
      </div>
      <form class="newdeal" data-new>
        <input name="name" placeholder="Neuer Deal: Bezeichnung, z. B. Rote-Bete-Kapseln Private Label" ${ro ? "disabled" : ""}>
        <input name="company" placeholder="Firma" value="${esc(m.companyName ?? "")}" ${ro ? "disabled" : ""}>
        <button class="btn primary" ${ro ? "disabled" : ""}>Deal anlegen</button>
      </form>
    </article>`;
}

const DONE: Record<string, string> = {
  anfrage: "Als neue Anfrage markiert",
  unklar: "Liegt jetzt unter „Mails zuordnen“",
  kein_bezug: "Als ohne Bezug markiert. Die Mail bleibt nur im Postfach.",
};

const mailOf = (el: Element) => {
  const card = el.closest<HTMLElement>("article.kf[data-mail]");
  return card ? { id: card.dataset.mail!, version: Number(card.dataset.version) } : null;
};
const findMail = (id: string) => [...S.unclear, ...S.pendingInquiries].find((m) => m.id === id);

/** Event handlers for mail cards, registered once. */
export function bindMailCards(): void {
  document.addEventListener("click", async (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("article.kf [data-assign], article.kf [data-decide], article.kf [data-original]");
    if (!t) return;
    const m = mailOf(t);
    if (!m) return;
    if (t.dataset.original !== undefined) return void showOriginal(m.id);
    if (t.dataset.assign) return void write(() => S.api.decideMail(m.id, m.version, { action: "assign", dealId: t.dataset.assign! }), "Zugeordnet. Die KI lernt aus der Entscheidung.");
    const action = t.dataset.decide as "anfrage" | "unklar" | "kein_bezug";
    await write(() => S.api.decideMail(m.id, m.version, { action }), DONE[action]);
  });
  document.addEventListener("change", async (e) => {
    const t = e.target as HTMLSelectElement;
    if (!t.matches("article.kf select[data-pick]") || !t.value) return;
    const m = mailOf(t);
    if (m) await write(() => S.api.decideMail(m.id, m.version, { action: "assign", dealId: t.value }), "Zugeordnet. Die KI lernt aus der Entscheidung.");
  });
  document.addEventListener("submit", async (e) => {
    const f = e.target as HTMLFormElement;
    if (!f.matches("article.kf form[data-new]")) return;
    e.preventDefault();
    const m = mailOf(f);
    const mail = m && findMail(m.id);
    if (!m || !mail) return;
    const name = (f.elements.namedItem("name") as HTMLInputElement).value.trim();
    const company = (f.elements.namedItem("company") as HTMLInputElement).value.trim();
    if (!name || !company) return toast("Bitte Bezeichnung und Firma eingeben.");
    // A second click or Enter while the first runs must not create a second deal.
    if (f.dataset.busy) return;
    f.dataset.busy = "1";
    f.querySelectorAll<HTMLButtonElement>("button").forEach((b) => (b.disabled = true));
    let dealId: string | null = null;
    await write(async () => {
      const deal = await S.api.createDeal({
        name,
        ...(mail.companyId && mail.companyName === company ? { companyId: mail.companyId } : { companyName: company }),
        contacts: mail.direction === "in" ? [{ name: mail.fromName ?? undefined, email: mail.fromAddr }] : [],
        nextAction: { text: "Anfrage beantworten", due: today() },
      });
      dealId = deal.id;
      await S.api.decideMail(m.id, m.version, { action: "assign", dealId: deal.id });
    }, "Deal angelegt und Mail zugeordnet");
    delete f.dataset.busy;
    if (dealId) location.hash = `deal-${dealId}`;
  });
}
